<script setup lang="ts">
import { ref, computed } from 'vue';
import { v5 as uuidv5 } from 'uuid';
import { GradientPoint, pSBC } from '@/lib/marker_colors';


type IndexedGradientPoint = GradientPoint & { id: string }

const props = defineProps<{
  colors: Array<GradientPoint>,
  width: number,
  height: number,
  horizontal?: boolean
}>();

const colors = computed((): Array<IndexedGradientPoint> => {
  return props.colors.map((color) => ({ ...{ offset: '0%', opacity: 1, id: self.crypto.randomUUID() }, ...color }));
});

const marker_path =
  // versione in alto a dx
"M479,447.77,268.43,56.64a8,8,0,0,0-14.09,0L43.73,447.77a8,8,0,0,0,7.05,11.79H472A8,8,0,0,0,479,447.77ZM281.38,411.48h-40v-40h40Zm-4-63.92h-32l-6-160h44Z"
//  "M 9 7 L 11 12 L 9 17 H 11 L 12 14.5 L 13 17 H 15 L 13 12 L 15 7 H 13 L 12 9.5 L 11 7 H 9 M 12 2 A 10 10 0 0 1 22 12 A 10 10 0 0 1 12 22 A 10 10 0 0 1 2 12 A 10 10 0 0 1 12 2 Z";
//  "M9,7L11,12L9,17H11L12,14.5L13,17H15L13,12L15,7H13L12,9.5L11,7H9M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2Z";
  //  "M 238.9336 0 c -131.96 0 -238.9336 106.9912 -238.9336 238.9336 C 0 370.8936 238.9336 783.7062 238.9336 783.7062 S 477.8672 370.8928 477.8672 238.9336 c 0 -131.9424 -106.9744 -238.9336 -238.9336 -238.9336 z m 70 244.4l 50 50a 40 40 90 0 1 -69 69l -50 -50l -50 50a 40 40 90 0 1 -69 -69l 50 -50l -50 -50a 40 40 90 0 1 69 -69l 50 50l 50 -50a 40 40 90 0 1 69 69z";
// "M305.095,229.104L186.055,42.579c-6.713-10.52-18.172-16.801-30.652-16.801c-12.481,0-23.94,6.281-30.651,16.801L5.711,229.103c-7.145,11.197-7.619,25.39-1.233,37.042c6.386,11.647,18.604,18.883,31.886,18.883h238.079c13.282,0,25.5-7.235,31.888-18.886C312.714,254.493,312.24,240.301,305.095,229.104z M155.403,253.631c-10.947,0-19.82-8.874-19.82-19.82c0-10.947,8.874-19.821,19.82-19.821c10.947,0,19.82,8.874,19.82,19.821C175.223,244.757,166.349,253.631,155.403,253.631z M182.875,115.9l-9.762,65.727c-1.437,9.675-10.445,16.353-20.119,14.916c-7.816-1.161-13.676-7.289-14.881-14.692l-10.601-65.597c-2.468-15.273,7.912-29.655,23.185-32.123c15.273-2.468,29.655,7.912,32.123,23.185C183.284,110.192,183.268,113.161,182.875,115.9z"
// "M 238.9336 0 c -131.96 0 -238.9336 106.9912 -238.9336 238.9336 C 0 370.8936 238.9336 783.7062 238.9336 783.7062 S 477.8672 370.8928 477.8672 238.9336 c 0 -131.9424 -106.9744 -238.9336 -238.9336 -238.9336 z m 0 334.4 a 63.4664 63.4664 90 1 1 0 -182.9328 a 63.4664 63.4664 90 0 1 0 182.9328 z"
const marker_viewbox = 
  // "0 0 477.86 833.7062";
  "0 0 512 512"
  //"0 0 24 24";
const x2 = ref<string>(!!props.horizontal ? '100%' : '0%');
const y2 = ref<string>(!props.horizontal ? '100%' : '0%');

const uuid_namespace = 'fff43bc6-9fb2-4db4-856d-2af5a56a4c5c';

const make_id = computed(() => {
  const hor = props.horizontal;

  function encode({ offset, color, opacity }: GradientPoint) {
    return color + `${offset}` + `${opacity}`;
  }

  const hashes = colors.value.map(encode);

  let res = `${hor}`;
  for (const hash of hashes) {
    res += '-' + hash;
  }
  return uuidv5(res, uuid_namespace);
});
const fill_id = computed(() => {
  return 'fill-' + make_id.value;
});
const stroke_id = computed(() => {
  return 'stroke-' + make_id.value;
});
</script>

<template>
  <svg :viewBox="marker_viewbox" :width="width" :height="height" class="icon" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient :id="fill_id" x1="0%" y1="0%" :x2="x2" :y2="y2">
        <stop
          v-for="gradient of colors"
          :key="gradient.id"
          :offset="gradient.offset"
          :style="`stop-color: ${gradient.color}; stop-opacity: ${gradient.opacity}`"
        />
      </linearGradient>
      <linearGradient :id="stroke_id" x1="0%" y1="0%" :x2="x2" :y2="y2">
        <stop
          v-for="gradient of colors"
          :key="gradient.id"
          :offset="gradient.offset"
          :style="`stop-color: ${pSBC(-0.6, gradient.color, undefined, undefined)}; stop-opacity: ${gradient.opacity}`"
        />
      </linearGradient>
      <filter id="icon-shadow-alert">
        <feDropShadow dx="1" dy="1" stdDeviation="3" flood-opacity="0.5"/>
      </filter>
      <filter id="featheredEllipse-alert" x="-50%" y="-50%" width="200%" height="200%">
        <feFlood result="floodFill" flood-color="black"/>
        <feComposite in="floodFill" in2="SourceAlpha" operator="in"/>
        <feGaussianBlur in="SourceGraphic" stdDeviation="10"/>
      </filter>
    </defs>
    <ellipse
      :cx="477.86 / 2" :cy="783.7062" rx="170" ry="40"
      fill="black" fill-opacity="0.2" filter="url(#featheredEllipse-alert)"
    />
    <path
      :d="marker_path"
      :fill="`url(#${fill_id})`"
      :stroke="`url(#${stroke_id})`"
    />
    <!-- :stroke="pSBC(-0.4, color as string) as string" -->
    <!-- d="M512 85.333c-164.95 0-298.667 133.739-298.667 298.667C213.333 548.95 512 938.667 512 938.667S810.667 548.949 810.667 384c0-164.928-133.718-298.667-298.667-298.667zm0 448a149.333 149.333 0 1 1 0-298.666 149.333 149.333 0 0 1 0 298.666z" -->
  </svg>

  <!-- EDIT HERE: https://editsvgcode.com/ -->
</template>

<style scoped lang="scss">
</style>

<!-- vim: softtabstop=2:shiftwidth=2
-->
